import { useTranslation } from '@cobliteam/i18n';
import { Skeleton } from '@cobliteam/ui/components';
import Icon from '@cobliteam/ui/icons';
import { Link, type LinkProps } from 'react-router-dom';
import useSearch from '../../hooks/useSearch';

type Image = {
  url?: string;
  alt?: string;
};

export interface CardImagesProps {
  isLoading: boolean;
  images: Image[] | [];
  moreLinkLabel: string;
  moreLink: LinkProps['to'];
  notFoundMessage: string;
  notFoundLinkLabel: string;
  notFoundLink: LinkProps['to'];
}

const CardImages = ({ isLoading, images, moreLink, notFoundLink, moreLinkLabel, notFoundMessage, notFoundLinkLabel }: CardImagesProps) => {
  const { t } = useTranslation('global-search');
  const { setIsOpen } = useSearch();
  if (isLoading) {
    return (
      <div className='flex flex-col'>
        <div className='flex justify-end'>
          <Skeleton className='mb-1 h-2 w-6 bg-gray-4' />
        </div>
        <div className='grid grid-cols-3 gap-2'>
          <Skeleton className='h-16 w-full bg-gray-4' />
          <Skeleton className='h-16 w-full bg-gray-4' />
          <Skeleton className='h-16 w-full bg-gray-4' />
        </div>
      </div>
    );
  }

  if (!images.length) {
    return (
      <div className='fade-in flex h-[76px] animate-in flex-col items-center justify-center rounded-md border border-gray-4 bg-gray-2 duration-300'>
        <div className='flex flex-row items-center justify-center gap-1 text-xs'>
          <div className='w-10'>
            <Icon name='image-off' size='lg' color='gray-light' />
          </div>
          <div className='flex flex-1 flex-col gap-2'>
            <div>{notFoundMessage}</div>
            <div className='-mt-2.5 w-full'>
              <Link className='font-medium text-[10px] text-gray-9 underline' to={notFoundLink} onClick={() => setIsOpen(false)}>
                {notFoundLinkLabel}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col'>
      <div className='group flex justify-end'>
        <Link className=' flex items-center font-medium text-[10px] text-gray-10 group-hover:underline' to={moreLink}>
          {moreLinkLabel} <Icon name='arrow-up-right' size='xs' className='!group-hover:text-gray-12' />
        </Link>
      </div>
      <div className='grid grid-cols-3 gap-2'>
        {images.map((image) => {
          if (!image.url) return null;
          return (
            <div key={image.alt} className='flex flex-col gap-2 rounded-md'>
              <img src={image.url} alt={image.alt} className='rounded-md' />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CardImages;
